import { useEffect, useState, useCallback } from 'react';

// Cache mémoire partagé entre tous les consommateurs
const cache = new Map();

const STORAGE_PREFIX = 'cap_ref_';
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const readSession = (key) => {
    try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
        if (!raw) return null;
        const { data, expiresAt } = JSON.parse(raw);
        if (Date.now() > expiresAt) return null;
        return { data, expiresAt };
    } catch { return null; }
};

const writeSession = (key, data, ttl) => {
    try {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({ data, expiresAt: Date.now() + ttl }));
    } catch { /* quota plein, ignore */ }
};

// Hook : récupère une liste de référentiel cachée (5 min par défaut).
// - `key` : identifiant unique du référentiel (ex: 'agences', 'compagnies')
// - `fetcher` : fonction async qui retourne la liste
// - `ttl` : durée de vie du cache en ms
//
// Pattern stale-while-revalidate :
//   1. Si cache mémoire valide → return immédiatement
//   2. Sinon, charge depuis sessionStorage et return ces données
//   3. En parallèle, refresh depuis le serveur si expiré ou absent
//
// Retourne : { data, loading, refresh }
const useReferentiel = (key, fetcher, ttl = DEFAULT_TTL_MS) => {
    const fromMem = cache.get(key);
    const initialData = fromMem?.data ?? readSession(key)?.data ?? [];
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(!fromMem && initialData.length === 0);

    const refresh = useCallback(async (force = false) => {
        const mem = cache.get(key);
        if (!force && mem && Date.now() < mem.expiresAt) {
            setData(mem.data);
            setLoading(false);
            return mem.data;
        }
        setLoading(true);
        try {
            const fresh = await fetcher();
            const list = Array.isArray(fresh) ? fresh : (fresh?.data || fresh?.items || []);
            const expiresAt = Date.now() + ttl;
            cache.set(key, { data: list, expiresAt });
            writeSession(key, list, ttl);
            setData(list);
            return list;
        } catch (e) {
            // Garde l'ancien data en cas d'erreur
            return data;
        } finally {
            setLoading(false);
        }
    }, [key, fetcher, ttl, data]);

    useEffect(() => { refresh(); }, [refresh]);

    return { data, loading, refresh };
};

// Invalide manuellement un cache (ex : après create/update/delete d'une agence)
export const invalidateReferentiel = (key) => {
    cache.delete(key);
    try { sessionStorage.removeItem(STORAGE_PREFIX + key); } catch { /* */ }
};

export default useReferentiel;
